<template>
  <div>
    <div
      v-if="te(`setupHub.subSteps.${subStep}.bannerText`)"
      class="mb-5 text-green-dark d-flex align-start"
      data-testid="setup-hub-generic-banner"
    >
      <v-icon icon="$protectionXs" />
      <div class="ml-1" v-html="$t(`setupHub.subSteps.${subStep}.bannerText`)" />
    </div>

    <img class="mb-2" data-testid="setup-hub-generic-illustration" :src="illustration" />

    <div
      data-testid="setup-hub-generic-description-title"
      v-if="te(`setupHub.subSteps.${subStep}.descriptionTitle`)"
      class="subtitle1 text--semibold mb-2"
      v-html="$t(`setupHub.subSteps.${subStep}.descriptionTitle`)"
    />

    <div
      data-testid="setup-hub-generic-description"
      class="body2 text-indigo-medium"
      v-html="$t(`setupHub.subSteps.${subStep}.description`)"
    />

    <v-btn
      data-testid="setup-hub-generic-step-btn"
      rounded
      color="primary"
      class="mt-6 mb-6"
      @click="onActionClick()"
    >
      {{ $t(`setupHub.subSteps.${subStep}.actionBtn`) }}
    </v-btn>

    <div
      data-testid="setup-hub-generic-help-article"
      class="d-flex align-center"
      @click="openHelpArticle()"
    >
      <div class="mr-1 body2 coro-link">{{ $t("setupHub.readHelpArticle") }}</div>
      <v-icon icon="$externalLink" size="14" />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { SetupHubStep, setupHubSubStepLinks } from "@/constants/setup-hub.ts";
import {
  getAddAccessPermissionsDialogConfig,
  getAddAdminUserDialogConfig,
  getAddLabelDialogConfig,
  getSetIndustryDialogConfig,
  getSetUserDataIndustryDialogConfig,
  openConnectCloudAppsModal,
} from "@/_helpers/dialog-helpers.ts";
import { type DialogConfig, useDialogsStore } from "@/_store/dialogs.module.ts";
import { useAdminUsersStore } from "@/_store/admin-users.module.ts";
import { ServiceAction } from "@/constants/cloud-apps.ts";
import { useSidebarPanelStore } from "@/_store/sidebar-panel.module.ts";
import { useI18n } from "vue-i18n";
import kebabCase from "lodash/kebabCase";
import { useSecurityAwarenessEmptyState } from "@/composables/useSecurityAwarenessEmptyState.ts";
import { LabelAction } from "@/constants/devices.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

export default defineComponent({
  props: {
    subStep: {
      type: String as PropType<SetupHubStep>,
      required: true,
    },
  },
  setup(props) {
    const dialogsStore = useDialogsStore();
    const adminAccountStore = useAdminUsersStore();
    const sidebarPanelStore = useSidebarPanelStore();
    const { handleAllowButtonClick } = useSecurityAwarenessEmptyState();
    const i18n = useI18n();

    const openHelpArticle = () => {
      trackGtmEvent(
        GtmAction.SETUP_HUB,
        `${startCase(props.subStep)} - ${GTMParams.HELP_LINK_CLICKED}`
      );
      window.open(setupHubSubStepLinks[props.subStep], "_blank");
    };

    const illustration = computed(() => {
      return `/images/setup-hub/${kebabCase(props.subStep)}.svg`;
    });

    const onActionClick = () => {
      let dialogConfig = {} as DialogConfig;
      trackGtmEvent(
        GtmAction.SETUP_HUB,
        `${startCase(props.subStep)} - ${GTMParams.ACTION_CLICKED}`
      );
      switch (props.subStep) {
        case SetupHubStep.INVITE_TEAMMATES:
          dialogConfig = getAddAdminUserDialogConfig(adminAccountStore.addAdminUser);
          break;
        case SetupHubStep.CONNECT_CLOUD_APP:
          openConnectCloudAppsModal(ServiceAction.CONNECT, {});
          sidebarPanelStore.closeSidebar();
          return;
        case SetupHubStep.SETUP_ACCESS_PERMISSIONS:
          dialogConfig = getAddAccessPermissionsDialogConfig();
          break;
        case SetupHubStep.ACTIVATE_SAT:
          handleAllowButtonClick();
          sidebarPanelStore.closeSidebar();
          return;
        case SetupHubStep.ADD_DEVICE_LABELS:
          dialogConfig = getAddLabelDialogConfig(LabelAction.CREATE_LABEL);
          break;
        case SetupHubStep.DEFINE_DEVICE_DATA_MONITORING_RULES:
          dialogConfig = getSetIndustryDialogConfig();
          break;
        case SetupHubStep.DEFINE_USER_DATA_MONITORING_RULES:
          dialogConfig = getSetUserDataIndustryDialogConfig();
          break;
        case SetupHubStep.ADD_DEVICE_POSTURE_SETTINGS:
        case SetupHubStep.CREATE_NETWORK_VIRTUAL_SERVER:
        case SetupHubStep.SETUP_VPN_ACCESS:
        case SetupHubStep.CREATE_SWG_VIRTUAL_SERVER:
        case SetupHubStep.ENABLE_DNS_FILTERING:
          sidebarPanelStore.closeSidebar();
          return;
      }
      sidebarPanelStore.closeSidebar();
      dialogsStore.openDialog(dialogConfig);
    };

    return {
      openHelpArticle,
      onActionClick,
      illustration,
      te: i18n.te,
    };
  },
});
</script>

<style scoped lang="scss">
.icon-protection-xs::before {
  color: rgb(var(--v-theme-green-dark));
}
</style>
